import wsStorage from "lib/wsStorage";
import {
    Store,
    RecordSource,
    Environment,
    Network,
    RequestParameters,
    Variables,
    GraphQLResponse,
    UploadableMap,
} from "relay-runtime";

import { redirectWithCurrentPage } from "lib/login";
import rollbar from "lib/rollbar";
import type { CacheConfig, FetchFunction } from "relay-runtime";

const HTTP_ENDPOINT = "/_e/graphql";

function sleep(duration: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, duration);
    });
}

type ErrorType = {
    message?: string;
    locations?: { line: number; column: number }[];
    path?: string[];
};
type MaybeError = GraphQLResponse & { errors?: ErrorType[] };

const fetchFn: FetchFunction = async (
    request: RequestParameters,
    variables: Variables,
    _cacheConfig: CacheConfig,
    uploadables: UploadableMap | null | undefined,
) => {
    let response: Response;

    if (uploadables && Object.keys(uploadables).length > 0) {
        // handle uploadables map creation
        const map = Object.keys(uploadables).reduce(
            (acc, key) => {
                acc[key] = [`variables.input.${key}`];
                return acc;
            },
            {} as Record<string, string[]>,
        );

        // build relay form data
        const formData = new FormData();
        formData.append("operations", JSON.stringify({ query: request.text, variables }));
        formData.append("map", JSON.stringify(map));

        Object.entries(uploadables).forEach(([k, v]) => {
            formData.append(k, v);
        });

        response = await fetch(HTTP_ENDPOINT, {
            method: "POST",
            headers: {
                Accept: "application/graphql-response+json; charset=utf-8, application/json; charset=utf-8",
                "X-CSRF-Token": window.csrfToken,
            },
            body: formData,
        });
    } else {
        response = await fetch(HTTP_ENDPOINT, {
            method: "POST",
            headers: {
                Accept: "application/graphql-response+json; charset=utf-8, application/json; charset=utf-8",
                "Content-Type": "application/json",
                "X-CSRF-Token": window.csrfToken,
            },
            body: JSON.stringify({
                query: request.text, // <-- The GraphQL document composed by Relay
                variables,
            }),
        });
    }

    const { headers } = response;

    const location = headers?.get("Location");

    // graphql expects to always get a 200, and browser fetch
    // implementations return stuff like "TypeError: Failed to fetch" when the
    // response is a 302, so we just check for a location header
    if (location) {
        redirectWithCurrentPage(location);
    }

    const result = (await response.json()) as MaybeError;
    if (result.errors && result.errors.length > 0) {
        // make a rollbar message that is actually relevant to this particular error
        // We're still going to die later if this is a non-nullable field that errored out,
        // but at least we'll know what the error was.
        rollbar.error(
            `Relay error: ${result.errors[0].message || "no message"} in ${
                result.errors[0].path?.join(",") || "no path"
            }`,
            {
                errors: result.errors,
                query: request.text,
                variables,
            },
        );
    }

    // localStorage key __ws_GQL_DELAY
    const rawDelay = wsStorage.getItem("GQL_DELAY");
    const delay = rawDelay ? parseInt(rawDelay, 10) : 0;
    if (delay) {
        console.warn("GQL_DELAY", delay); // eslint-disable-line
        await sleep(delay);
    }

    return result;
};

function createRelayEnvironment() {
    return new Environment({
        network: Network.create(fetchFn),
        store: new Store(new RecordSource(), {
            queryCacheExpirationTime: 5 * 60 * 1000, // 5 minutes
        }),
    });
}

export const EmployeeRelayEnvironment = createRelayEnvironment();
